import {
  Button,
  Chip,
  Dropdown,
  Modal,
  Skeleton,
} from "@heroui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  Link2,
  MapPin,
  MoreHorizontal,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import CancelOrderModal from "@/components/order/modal/cancel-order";
import { OrderRefundModal } from "@/components/order/modal/refund";
import { RescheduleDeliveryModal } from "@/components/order/modal/reschedule-delivery";
import { AttributionDetails } from "@/components/attribution";
import { ImageTile, notify } from "@/components/kit";
import {
  DeliveryTypeChip,
  PersonAvatar,
  StatusChip,
  aed,
  formatDay,
  formatDayTime,
} from "@/components/sales/shared";
import { ORDER_CACHE_KEYS } from "@/hooks/api/order/keys";
import { useCancelOrder } from "@/hooks/api/order/useCancelOrder";
import { OrderStatus } from "@/enums/order";
import { codPaymentUrl } from "@/repositories/order";
import { OrderDetailResponse } from "@/types/order-detail.interface";
import { isOrderPaid } from "@/utils/order";
import { promotionValueText } from "@/utils/promotion";

interface OrderDetailsProps {
  data: OrderDetailResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  handleNavigate: (path: string) => void;
}

const STEPS = [
  OrderStatus.PENDING,
  OrderStatus.PAID,
  OrderStatus.PACKAGED,
  OrderStatus.ENROUTE,
  OrderStatus.DELIVERED,
];
const STEP_LABEL: Record<string, string> = {
  [OrderStatus.PENDING]: "Placed",
  [OrderStatus.ENROUTE]: "En route",
};
const label = (status: string) =>
  STEP_LABEL[status] ?? status.charAt(0).toUpperCase() + status.slice(1);

const LIFECYCLE = [
  OrderStatus.AWAITING_PAYMENT,
  OrderStatus.PENDING,
  OrderStatus.PAID,
  OrderStatus.PACKAGED,
  OrderStatus.ENROUTE,
  OrderStatus.DELIVERED,
  OrderStatus.FAILED,
  OrderStatus.CANCELLED,
];

const RESCHEDULABLE_STATUSES = [
  OrderStatus.PENDING,
  OrderStatus.PAID,
  OrderStatus.PACKAGED,
];

const COD_PAYABLE_STATUSES = [OrderStatus.PACKAGED, OrderStatus.ENROUTE];

const same = (a: string, b: string) =>
  a.toLowerCase().replace(/[-_ ]/g, "") === b.toLowerCase().replace(/[-_ ]/g, "");

function Panel({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-surface-secondary flex flex-col gap-3 rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Stepper({
  data,
  isPaid,
}: {
  data: OrderDetailResponse;
  isPaid: boolean;
}) {
  const statusIndex = STEPS.findIndex((step) => same(step, data.status));
  const lastReached = Math.max(
    statusIndex,
    ...STEPS.map((step, index) =>
      data.timelines.some((t) => same(t.status, step)) ? index : -1,
    ),
  );

  return (
    <ol
      aria-label="Order progress"
      className="bg-surface-secondary grid grid-cols-5 rounded-2xl px-4 py-6"
    >
      {STEPS.map((step, index) => {
        const entry = data.timelines.find((t) => same(t.status, step));
        // COD orders reach later steps before payment, so Paid needs its own evidence.
        const skipped = step === OrderStatus.PAID && !isPaid;
        const reached = !skipped && index <= lastReached;
        const done = !skipped && (index < lastReached || (index === lastReached && index === STEPS.length - 1));
        const current = !skipped && index === lastReached && !done;

        return (
          <li key={step} className="relative flex flex-col items-center gap-1">
            {index > 0 ? (
              <span
                aria-hidden="true"
                className={`absolute end-1/2 top-3.5 h-0.5 w-full ${
                  reached ? "bg-accent" : "bg-separator"
                }`}
              />
            ) : null}
            <span
              className={`relative z-10 flex size-7 items-center justify-center rounded-full border-2 text-xs font-medium ${
                done
                  ? "border-accent bg-accent text-accent-foreground"
                  : current
                    ? "border-accent bg-surface-secondary text-accent"
                    : "border-separator bg-surface-secondary text-muted"
              }`}
            >
              {done ? <Check className="size-3.5" /> : index + 1}
            </span>
            <span
              className={`text-sm ${reached ? "font-medium" : "text-muted"}`}
            >
              {label(step)}
            </span>
            <span className="text-muted h-4 text-xs tabular-nums">
              {entry ? formatDayTime(entry.createdAt) : ""}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

const itemStatusLabel = (status: string) =>
  status.toLowerCase().replace(/_/g, "-") === "out-of-stock"
    ? "Out of stock"
    : undefined;

function ItemsTable({
  data,
  onImages,
}: {
  data: OrderDetailResponse;
  onImages: (images: string[]) => void;
}) {
  const units = data.items.reduce((sum, item) => sum + item.quantity, 0);
  const th = "text-muted px-3 py-2 text-xs font-medium";

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold">Items</h2>
        <span className="text-muted text-sm">
          {data.items.length} item{data.items.length === 1 ? "" : "s"} · {units}{" "}
          unit{units === 1 ? "" : "s"}
        </span>
      </div>
      {data.items.length === 0 ? (
        <p className="text-muted py-8 text-center text-sm">
          No items in this order
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-separator border-b">
                <th className={`${th} text-start`}>Item</th>
                <th className={`${th} text-start`}>Status</th>
                <th className={`${th} text-end`}>Qty</th>
                <th className={`${th} text-end`}>Buy</th>
                <th className={`${th} text-end`}>Sell</th>
                <th className={`${th} text-end`}>Total</th>
              </tr>
            </thead>
            <tbody className="divide-separator divide-y">
              {data.items.map((item, index) => {
                const name = item.product?.name || item.name;
                const images = item.product?.images ?? [];
                const buy = parseFloat(item.buyPrice);
                const sell = parseFloat(item.sellPrice);
                const listTotal =
                  item.listPrice === null
                    ? null
                    : item.listPrice * item.quantity;

                return (
                  <tr key={index}>
                    <td className="min-w-64 px-3 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          aria-label={`View images of ${name}`}
                          className="shrink-0 disabled:cursor-default"
                          disabled={images.length === 0}
                          type="button"
                          onClick={() => onImages(images)}
                        >
                          <ImageTile src={images[0]} />
                        </button>
                        <div className="flex min-w-0 flex-col leading-snug">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{name}</span>
                            {item.isSimilarProduct ? (
                              <Chip color="accent" variant="soft">
                                Similar
                              </Chip>
                            ) : item.isSpecialProduct ? (
                              <Chip color="warning" variant="soft">
                                Special
                              </Chip>
                            ) : null}
                          </span>
                          {item.product?.nameAr ? (
                            <span className="text-muted block text-xs">
                              <bdi dir="rtl" lang="ar">
                                {item.product.nameAr}
                              </bdi>
                            </span>
                          ) : null}
                          {item.promotion ? (
                            <Chip
                              className="mt-1 self-start"
                              color="success"
                              size="sm"
                              variant="soft"
                            >
                              {item.promotion.name}
                            </Chip>
                          ) : null}
                          {item.outOfStockReason ? (
                            <span className="text-muted text-xs">
                              Out of stock: {item.outOfStockReason}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 whitespace-nowrap">
                      <StatusChip
                        label={itemStatusLabel(item.status)}
                        status={item.status}
                      />
                    </td>
                    <td className="px-3 text-end tabular-nums">
                      {item.quantity}
                    </td>
                    <td className="px-3 text-end whitespace-nowrap tabular-nums">
                      {buy > 0 ? (
                        <span className="text-muted">{buy.toFixed(2)}</span>
                      ) : (
                        <span className="text-muted">Not set</span>
                      )}
                    </td>
                    <td className="px-3 text-end whitespace-nowrap tabular-nums">
                      {sell > 0 ? (
                        sell.toFixed(2)
                      ) : (
                        <span className="text-muted">Not set</span>
                      )}
                    </td>
                    <td className="px-3 text-end font-medium whitespace-nowrap tabular-nums">
                      {listTotal !== null && listTotal > item.totalPrice ? (
                        <span className="text-muted block text-xs font-normal line-through">
                          {aed(listTotal)}
                        </span>
                      ) : null}
                      {aed(item.totalPrice)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Timeline({ data }: { data: OrderDetailResponse }) {
  // Newest first; events with the same time go latest status first.
  const rank = (status: string) =>
    LIFECYCLE.findIndex((step) => same(step, status));
  const timelines = [...data.timelines].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() ||
      rank(b.status) - rank(a.status),
  );

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">Timeline</h2>
      {data.timelines.length === 0 ? (
        <p className="text-muted text-sm">No timeline events yet</p>
      ) : (
        <ol className="bg-surface-secondary flex flex-col rounded-2xl p-5">
          {timelines.map((entry, index) => (
            <li key={entry.id} className="flex gap-4">
              <div className="flex flex-col items-center">
                <span
                  className={`mt-1.5 size-3 shrink-0 rounded-full border-2 ${
                    index === 0
                      ? "border-accent bg-accent/30"
                      : "border-separator bg-surface"
                  }`}
                />
                {index < timelines.length - 1 ? (
                  <span className="bg-separator w-px flex-1" />
                ) : null}
              </div>
              <div className="flex min-w-0 flex-1 flex-col pb-4 leading-snug">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="font-medium capitalize">{entry.status}</span>
                  <span className="text-muted text-xs tabular-nums">
                    {formatDayTime(entry.createdAt)}
                  </span>
                </div>
                <span className="text-muted text-sm">
                  {[entry.notes, entry.updatedBy].filter(Boolean).join(" · ")}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Gallery({
  images,
  onClose,
}: {
  images: string[];
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);

  return (
    <Modal.Backdrop
      isOpen={images.length > 0}
      variant="opaque"
      onOpenChange={(open) => !open && onClose()}
    >
      <Modal.Container placement="center">
        <Modal.Dialog className="w-full max-w-xl">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>
              Image {index + 1} of {images.length}
            </Modal.Heading>
          </Modal.Header>
          <Modal.Body className="flex flex-col items-center gap-4 p-1">
            <img
              alt="Product"
              className="max-h-[60vh] rounded-xl object-contain"
              src={images[index]}
            />
            {images.length > 1 ? (
              <div className="flex gap-2">
                {images.map((src, i) => (
                  <button
                    key={src}
                    aria-label={`Show image ${i + 1}`}
                    className={`size-12 overflow-hidden rounded-lg border-2 ${i === index ? "border-accent" : "border-transparent"}`}
                    type="button"
                    onClick={() => setIndex(i)}
                  >
                    <img alt="" className="size-full object-cover" src={src} />
                  </button>
                ))}
              </div>
            ) : null}
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function PageSkeleton() {
  return (
    <div aria-busy="true" className="flex flex-col gap-6" role="status">
      <Skeleton className="h-5 w-20 rounded-md" />
      <Skeleton className="h-10 w-64 rounded-lg" />
      <Skeleton className="h-28 w-full rounded-2xl" />
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <Skeleton className="h-72 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    </div>
  );
}

export const OrderDetails = ({
  data,
  isLoading,
  isError,
  handleNavigate,
}: OrderDetailsProps) => {
  const queryClient = useQueryClient();
  const [images, setImages] = useState<string[]>([]);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isRefundOpen, setIsRefundOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const { mutate: cancelOrder, isPending: isCancelling } = useCancelOrder();

  if (isLoading) return <PageSkeleton />;

  if (isError || !data) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
        <h2 className="text-lg font-semibold">
          {isError ? "Error Loading Order" : "Order Not Found"}
        </h2>
        <p className="text-muted mb-4 text-sm">
          {isError
            ? "There was an error loading the order details. Please try again."
            : "The requested order could not be found."}
        </p>
        <Button variant="secondary" onPress={() => handleNavigate("/orders")}>
          Back to Orders
        </Button>
      </div>
    );
  }

  const isPaid = isOrderPaid(data.timelines);
  const isCod = data.paymentProvider?.toLowerCase() === "cod";
  const paymentLabel = isCod ? "COD" : "Stripe";
  const paymentChannel = isCod && isPaid ? "payment link" : paymentLabel;
  const { user, address } = data;
  const userName = `${user.firstName} ${user.lastName}`.trim();
  const totalSaved = data.promotions.reduce(
    (sum, { discountAmount }) => sum + discountAmount,
    0,
  );
  const mapUrl = address?.geoPoint
    ? `https://www.google.com/maps?q=${address.geoPoint.coordinates[1]},${address.geoPoint.coordinates[0]}`
    : null;

  const handleCancelConfirm = () => {
    cancelOrder(data.id, {
      onSuccess: () => {
        notify.success(
          "Order cancelled",
          `Order #${data.displayId} has been cancelled`,
        );
        setIsCancelOpen(false);
        queryClient.invalidateQueries({
          queryKey: ORDER_CACHE_KEYS.GET(data.id),
        });
        queryClient.invalidateQueries({
          queryKey: ORDER_CACHE_KEYS.LIST({}),
        });
      },
      onError: () => notify.error("Failed to cancel order", "Please try again"),
    });
  };

  const handleCopyPaymentLink = () => {
    navigator.clipboard.writeText(codPaymentUrl(data.id)).then(
      () =>
        notify.success(
          "Payment link copied",
          `Send it to the customer to pay order #${data.displayId} online`,
        ),
      () => notify.error("Failed to copy payment link", "Please try again"),
    );
  };

  return (
    <div className="flex flex-col gap-6 pb-8">
      <div className="flex flex-col gap-4">
        <Button
          aria-label="Back to orders"
          className="-ms-2 w-fit"
          size="sm"
          variant="ghost"
          onPress={() => handleNavigate("/orders")}
        >
          <ChevronLeft className="size-4" />
          Orders
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">
                Order #{data.displayId}
              </h1>
              <StatusChip status={data.status} />
              {data.deliveryType ? (
                <DeliveryTypeChip type={data.deliveryType} />
              ) : null}
            </div>
            <p className="text-muted text-sm">
              {data.deliveryDate
                ? `Delivery due ${new Date(data.deliveryDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                : "No delivery date set"}
            </p>
          </div>
          <Dropdown>
            <Button aria-label="Order actions" variant="secondary">
              Actions
              <MoreHorizontal className="size-4" />
            </Button>
            <Dropdown.Popover placement="bottom end">
              <Dropdown.Menu aria-label="Order actions">
                <Dropdown.Item
                  id="reschedule"
                  isDisabled={
                    !RESCHEDULABLE_STATUSES.some((status) =>
                      same(status, data.status),
                    )
                  }
                  textValue="Change Delivery Date"
                  onAction={() => setIsRescheduleOpen(true)}
                >
                  <CalendarClock className="size-4" />
                  Change Delivery Date
                </Dropdown.Item>
                {isCod && !isPaid ? (
                  <Dropdown.Item
                    id="copy-payment-link"
                    isDisabled={
                      !COD_PAYABLE_STATUSES.some((status) =>
                        same(status, data.status),
                      )
                    }
                    textValue="Copy Payment Link"
                    onAction={handleCopyPaymentLink}
                  >
                    <Link2 className="size-4" />
                    Copy Payment Link
                  </Dropdown.Item>
                ) : null}
                <Dropdown.Item
                  id="refund"
                  textValue="Refund Order"
                  onAction={() => setIsRefundOpen(true)}
                >
                  <RotateCcw className="size-4" />
                  Refund Order
                </Dropdown.Item>
                <Dropdown.Item
                  id="cancel"
                  textValue="Cancel Order"
                  variant="danger"
                  onAction={() => setIsCancelOpen(true)}
                >
                  <XCircle className="size-4" />
                  Cancel Order
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </div>
      </div>

      <Stepper data={data} isPaid={isPaid} />

      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex min-w-0 flex-col gap-8">
          <ItemsTable data={data} onImages={setImages} />
          <Timeline data={data} />
        </div>

        <aside className="flex flex-col gap-4">
          <Panel title="Summary">
            <dl className="flex flex-col gap-2.5 text-sm tabular-nums">
              <div className="flex justify-between">
                <dt className="text-muted">Subtotal</dt>
                <dd>{aed(data.subTotalAmount)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">VAT</dt>
                <dd>{aed(data.vatAmount)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">Delivery fee</dt>
                <dd>{aed(data.deliveryAmount)}</dd>
              </div>
              {data.codFee > 0 ? (
                <div className="flex justify-between">
                  <dt className="text-muted">COD fee</dt>
                  <dd>{aed(data.codFee)}</dd>
                </div>
              ) : null}
              {data.discountAmount > 0 ? (
                <div className="flex justify-between text-(--success)">
                  <dt>Discount</dt>
                  <dd>-{aed(data.discountAmount)}</dd>
                </div>
              ) : null}
              {data.codePromotion ? (
                <div className="flex justify-between">
                  <dt className="text-muted">Code used</dt>
                  <dd className="font-mono">{data.codePromotion.code}</dd>
                </div>
              ) : null}
              <div className="border-separator mt-1 flex items-baseline justify-between border-t pt-3">
                <dt className="font-semibold">Total</dt>
                <dd className="text-xl font-semibold">
                  {aed(data.totalAmount)}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted">Payment · {paymentLabel}</dt>
                <dd>
                  <Chip
                    color={isPaid ? "success" : "warning"}
                    variant="soft"
                  >
                    {isPaid ? "Paid" : "Pending"} via {paymentChannel}
                  </Chip>
                </dd>
              </div>
            </dl>
          </Panel>

          {data.promotions.length > 0 ? (
            <Panel title="Offers applied">
              <ul className="flex flex-col gap-3 text-sm">
                {data.promotions.map((promotion) => (
                  <li
                    key={`${promotion.promotionId}-${promotion.name}`}
                    className="flex items-start justify-between gap-3"
                  >
                    <span className="flex flex-col gap-1 leading-snug">
                      <span className="font-medium">{promotion.name}</span>
                      <span className="text-muted text-xs">
                        {promotionValueText(promotion)}
                      </span>
                      {data.codePromotion?.code &&
                      data.codePromotion.id === promotion.promotionId ? (
                        <Chip className="self-start" size="sm" variant="soft">
                          <span className="font-mono">
                            {data.codePromotion.code}
                          </span>
                        </Chip>
                      ) : null}
                    </span>
                    <span className="text-(--success) tabular-nums">
                      -{aed(promotion.discountAmount)}
                    </span>
                  </li>
                ))}
              </ul>
              {data.promotions.length > 1 ? (
                <div className="border-separator flex justify-between border-t pt-3 text-sm font-semibold tabular-nums">
                  <span>Total saved</span>
                  <span className="text-(--success)">-{aed(totalSaved)}</span>
                </div>
              ) : null}
            </Panel>
          ) : null}

          {data.linkedOrders.length > 0 ? (
            <Panel title="Linked orders">
              <ul className="flex flex-col gap-2 text-sm">
                {data.linkedOrders.map((linked) => (
                  <li
                    key={linked.id}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="flex flex-col leading-snug">
                      <Link
                        className="font-medium tabular-nums underline underline-offset-4"
                        to={`/orders/${linked.id}`}
                      >
                        #{linked.displayId}
                      </Link>
                      <span className="text-muted text-xs tabular-nums">
                        {linked.deliveryDate
                          ? formatDay(linked.deliveryDate)
                          : "No delivery date"}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      {linked.deliveryType ? (
                        <DeliveryTypeChip type={linked.deliveryType} />
                      ) : null}
                      <StatusChip status={linked.status} />
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          <Panel title="Attribution">
            <AttributionDetails attribution={data.attribution} />
          </Panel>

          <Panel title="Customer">
            <div className="flex items-center gap-3">
              <PersonAvatar name={userName} size="md" />
              <div className="flex min-w-0 flex-col leading-snug">
                <span className="font-medium">{userName}</span>
                <span className="text-muted truncate text-sm">{user.email}</span>
              </div>
            </div>
            <span className="text-sm tabular-nums">{user.phoneNumber}</span>
          </Panel>

          {address ? (
            <Panel
              aside={
                address.nickName ? (
                  <Chip variant="secondary">
                    {address.nickName}
                  </Chip>
                ) : null
              }
              title="Delivery Address"
            >
              <div className="flex flex-col gap-0.5 text-sm">
                <p>
                  {[
                    address.buildingName,
                    address.unitNumber && `Unit ${address.unitNumber}`,
                    address.street,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
                <p className="text-muted">
                  {[address.area, address.city].filter(Boolean).join(", ")}
                </p>
              </div>
              <p className="text-muted text-sm tabular-nums">
                {[
                  address.addressType &&
                    address.addressType.charAt(0).toUpperCase() +
                      address.addressType.slice(1),
                  address.phoneNumber,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {mapUrl ? (
                <a
                  className="border-border hover:bg-(--default) inline-flex h-8 w-fit items-center gap-1.5 rounded-full border px-3 text-sm"
                  href={mapUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <MapPin className="size-3.5" />
                  View on map
                  <ChevronRight className="size-3.5" />
                </a>
              ) : null}
            </Panel>
          ) : null}
        </aside>
      </div>

      {images.length > 0 ? (
        <Gallery images={images} onClose={() => setImages([])} />
      ) : null}

      <CancelOrderModal
        displayId={data.displayId}
        handleCancel={handleCancelConfirm}
        isLoading={isCancelling}
        isOpen={isCancelOpen}
        totalAmount={data.totalAmount}
        onClose={() => setIsCancelOpen(false)}
      />

      <RescheduleDeliveryModal
        key={isRescheduleOpen ? "open" : "closed"}
        deliveryDate={data.deliveryDate}
        displayId={data.displayId}
        isOpen={isRescheduleOpen}
        orderId={data.id}
        onClose={() => setIsRescheduleOpen(false)}
      />

      <OrderRefundModal
        key={isRefundOpen ? "open" : "closed"}
        displayId={data.displayId}
        isOpen={isRefundOpen}
        items={data.items.map((item) => ({
          id: item.product?.id,
          name: item.name || item.product.name,
          status: item.status,
          quantity: item.quantity,
          price: parseFloat(item.sellPrice),
        }))}
        orderId={data.id}
        totalAmount={data.totalAmount}
        onClose={() => setIsRefundOpen(false)}
      />
    </div>
  );
};
